'use client';

import { useHeartbeat } from '@/api/locations/useHeartbeat';
import { Avatar } from '@/components/Elements';
import { ScrollArea } from '@/components/ui/elements/scroll-area';
import { Sheet, SheetContent } from '@/components/ui/elements/sheet';
import { MainMenuItems } from '@/config/mainmenu';
import { PolicyGate, useAuthorization } from '@/features/auth/authorization';
import { AuthContext } from '@/providers/auth';
import { LocationContext } from '@/providers/location';
import { useTheme } from '@/providers/theme';
import { cn } from '@/utils/format';
import {
  ArrowLeftStartOnRectangleIcon,
  ComputerDesktopIcon,
  MoonIcon,
  SunIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { Link, LinkOptions } from '@tanstack/react-router';
import * as React from 'react';
import { useContext } from 'react';

interface MobileNavProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export function MobileNav({ open, setOpen }: MobileNavProps) {
  const { waiting, eventsWaiting, unreadMessages } = useHeartbeat();
  const { user, logout } = useContext(AuthContext);
  const { activeLocation } = useContext(LocationContext);
  const { theme, setTheme } = useTheme();
  const { checkPolicy } = useAuthorization();

  const userInitials = user ? `${user.first_name[0] ?? '?'}${user.last_name[0] ?? '?'}` : '??';

  const handleThemeChange = (nextTheme: 'light' | 'dark' | 'system') => {
    setTheme(nextTheme);
  };

  const handleSignOut = () => {
    setOpen(false);
    logout();
  };

  const menuItems = MainMenuItems.filter((item) => {
    return item.access_rights.every((right: string) => {
      return checkPolicy({ policy: right, meta: { location: activeLocation } });
    });
  });

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="left" className="pr-0">
        <MobileLink to="/" className="flex items-center" setOpen={setOpen}>
          <span className="font-bold">GameTruck Portal</span>
        </MobileLink>
        <ScrollArea className="my-4 h-[calc(100vh-8rem)] pb-10 pl-0">
          <div className="flex flex-col space-y-3">
            {menuItems?.map(
              (item) =>
                item.path && (
                  <MobileLink
                    key={item.path}
                    to={item.path}
                    search={item.search}
                    setOpen={setOpen}
                    className="flex items-center"
                  >
                    <span>{item.label}</span>
                    <PolicyGate policy="messages.view">
                      {item.label === 'Messages' && !!unreadMessages && (
                        <div className="bg-red-600 text-white rounded-3xl p-0.5 w-full max-w-8 mx-2 text-center text-xs font-bold">
                          {unreadMessages}
                        </div>
                      )}
                      {item.label === 'Leads' && !!waiting && (
                        <div className="bg-red-600 text-white rounded-3xl p-0.5 w-full max-w-8 mx-2 text-center text-xs font-bold">
                          {waiting}
                        </div>
                      )}
                      {item.label === 'Events' && !!eventsWaiting && (
                        <div className="bg-red-600 text-white rounded-3xl p-0.5 w-full max-w-8 mx-2 text-center text-xs font-bold">
                          {eventsWaiting}
                        </div>
                      )}
                    </PolicyGate>
                  </MobileLink>
                ),
            )}

            {user && (
              <div className="mt-4 border-t pt-4 pr-4 space-y-3">
                <div className="flex items-center gap-2">
                  <Avatar
                    alt={user.full_name}
                    src={user.avatar}
                    fallback={userInitials}
                    size="md"
                  />
                  <span className="text-sm font-semibold">{user.full_name}</span>
                </div>

                <MobileLink
                  to="/account/user-profile"
                  setOpen={setOpen}
                  className="flex items-center gap-2"
                >
                  <UserCircleIcon className="h-4 w-4" aria-hidden="true" />
                  <span>Profile</span>
                </MobileLink>

                <div className="space-y-1">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Theme
                  </p>
                  <div className="grid grid-cols-3 gap-1 rounded-md bg-muted p-1">
                    <button
                      type="button"
                      className={cn(
                        'inline-flex items-center justify-center rounded-sm px-2 py-1 text-xs',
                        theme === 'light' ? 'bg-background shadow-xs' : 'text-muted-foreground',
                      )}
                      onClick={() => handleThemeChange('light')}
                    >
                      <SunIcon className="h-4 w-4" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className={cn(
                        'inline-flex items-center justify-center rounded-sm px-2 py-1 text-xs',
                        theme === 'dark' ? 'bg-background shadow-xs' : 'text-muted-foreground',
                      )}
                      onClick={() => handleThemeChange('dark')}
                    >
                      <MoonIcon className="h-4 w-4" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className={cn(
                        'inline-flex items-center justify-center rounded-sm px-2 py-1 text-xs',
                        theme === 'system' ? 'bg-background shadow-xs' : 'text-muted-foreground',
                      )}
                      onClick={() => handleThemeChange('system')}
                    >
                      <ComputerDesktopIcon className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                </div>

                <button
                  type="button"
                  className="flex items-center gap-2 text-destructive"
                  onClick={handleSignOut}
                >
                  <ArrowLeftStartOnRectangleIcon className="h-4 w-4" aria-hidden="true" />
                  <span>Sign Out</span>
                </button>
              </div>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

interface MobileLinkProps extends LinkOptions {
  setOpen: (open: boolean) => void;
  children: React.ReactNode;
  className?: string;
}

function MobileLink({ to, setOpen, className, children, ...props }: MobileLinkProps) {
  return (
    <Link to={to} onClick={() => setOpen(false)} className={cn(className)} {...props}>
      {children}
    </Link>
  );
}
